import moment, { unitOfTime } from 'moment-timezone';

import { formatLocation } from './formatters';
import { ORDERED_JOB_STATUSES } from './job-status';

import { employeeName } from '../employees/utils/employee-name';

export const SORT_ORDER = {
  ASC: 1,
  DESC: -1,
};

export const sortByDate =
  <T>(dateFunc: (obj: T) => moment.MomentInput, sortOrder: number = SORT_ORDER.DESC) =>
  (a: T, b: T) => {
    const aDate = dateFunc(a);
    const bDate = dateFunc(b);
    return compareDates(aDate, bDate, sortOrder);
  };

export const compareDates = (
  aDateString: moment.MomentInput,
  bDateString: moment.MomentInput,
  sortOrder: number = SORT_ORDER.DESC,
  granularity?: unitOfTime.StartOf,
) => {
  if (aDateString === null) {
    return 1;
  }
  if (bDateString === null) {
    return -1;
  }
  const aMoment = moment(aDateString);
  const bMoment = moment(bDateString);
  const comparisonGranularity = granularity || 'millisecond';

  // Returns 1 if a > b, -1 if a < b, 0 if a = b
  return aMoment.isAfter(bMoment, comparisonGranularity)
    ? sortOrder
    : aMoment.isBefore(bMoment, comparisonGranularity)
    ? -1 * sortOrder
    : 0;
};

export const compareTimeZoneAdjustedTime = (
  a: { time: string; timeZoneId: string },
  b: { time: string; timeZoneId: string },
  sortOrder: number = SORT_ORDER.DESC,
) => {
  const adjustedTimeA = moment.tz(`${moment().format('YYYY-MM-DD')} ${a.time}`, a.timeZoneId).utc();
  const adjustedTimeB = moment.tz(`${moment().format('YYYY-MM-DD')} ${b.time}`, b.timeZoneId).utc();
  if (!adjustedTimeA.isValid() || !adjustedTimeB.isValid()) {
    throw new Error('Invalid Dates, times should be in the form hh:mm:ss or hh:mm');
  }

  return adjustedTimeA > adjustedTimeB ? sortOrder : sortOrder * -1;
};

export const sortAlphabetically =
  <T>(wordFunc: (obj: T) => string, sortOrder: number = SORT_ORDER.DESC) =>
  (a: T, b: T) => {
    const aWord = wordFunc(a);
    const bWord = wordFunc(b);
    return sortOrder === SORT_ORDER.ASC ? aWord.localeCompare(bWord) : bWord.localeCompare(aWord);
  };

export const applicationQualitySort =
  <T>(applicationFunc: (obj: T) => FullApplicant) =>
  (a: T, b: T) => {
    const aApp = applicationFunc(a);
    const bApp = applicationFunc(b);

    if (!aApp || !bApp) {
      return 0;
    }
    if (Number(Boolean(aApp.referralId)) - Number(Boolean(bApp.referralId)) !== 0) {
      return Number(Boolean(bApp.referralId)) - Number(Boolean(aApp.referralId));
    }
    if (Number(aApp.isHQC) - Number(bApp.isHQC) !== 0) {
      return Number(bApp.isHQC) - Number(aApp.isHQC);
    }
    if (bApp.automatedScore - aApp.automatedScore !== 0) {
      return bApp.automatedScore - aApp.automatedScore;
    }
    if (bApp.lastUpdateDate && aApp.lastUpdateDate) {
      return moment(bApp.lastUpdateDate).diff(moment(aApp.lastUpdateDate), 'day');
    }

    return 0;
  };

export const sortEmployeesAlphabetically = sortAlphabetically(
  (employee: Employee) => employeeName(employee),
  SORT_ORDER.ASC,
);

export const sortJobsByTitleActiveLocation = (jobs: Array<Job>): Array<Job> =>
  jobs.sort((job1, job2) => {
    const title1 = job1.title;
    const title2 = job2.title;
    const alphabeticalCompare = title1.toLowerCase().localeCompare(title2.toLowerCase());

    let activityCompare = 0;
    if (job1.status !== job2.status) {
      activityCompare = sortJobs(job1, job2);
    }

    const location1 = formatLocation({ city: job1.city, state: job1.state });
    const location2 = formatLocation({ city: job2.city, state: job2.state });
    let locationCompare = 0;
    if (location1 && !location2) {
      locationCompare = -1;
    }
    if (!location1 && location2) {
      locationCompare = 1;
    }
    if (location1 && location2 && location1.toLowerCase() !== location2.toLowerCase()) {
      locationCompare = location1.toLowerCase().localeCompare(location2.toLowerCase());
    }

    // Sorts by location, then activity, then alphabetically by name
    return locationCompare || activityCompare || alphabeticalCompare;
  });

export const sortJobs = (a: Job, b: Job) => {
  if (a.status !== b.status) {
    return ORDERED_JOB_STATUSES.indexOf(a.status) > ORDERED_JOB_STATUSES.indexOf(b.status) ? 1 : -1;
  }

  return a.title.localeCompare(b.title);
};

// Sorts with null and undefined values always last
export const sortByNullAwareField =
  <T>(sortBy: keyof T, direction: SortDirection = 'asc') =>
  (a: T, b: T) => {
    const value1 = a[sortBy];
    const value2 = b[sortBy];

    if (value1 === null || typeof value1 === 'undefined') {
      return 1;
    }

    if (value2 === null || typeof value2 === 'undefined') {
      return -1;
    }

    if (value1 === value2) {
      return 0;
    }

    if (direction === 'desc') {
      return value1 < value2 ? 1 : -1;
    }

    return value1 < value2 ? -1 : 1;
  };

export const sortBoostByStartAt = (a: JobBoost, b: JobBoost) =>
  new Date(a.startAt).valueOf() - new Date(b.startAt).valueOf();
